import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([]);

    // Configure axios defaults
    axios.defaults.baseURL = import.meta.env.VITE_API_URL || 'http://localhost:3333/api';
    axios.defaults.withCredentials = true;

    const fetchUser = async () => {
        try {
            const response = await axios.get('/auth/me');
            setUser(response.data);
            // Extraire les rôles
            const userRoles = response.data.roles ? response.data.roles.map(r => r.name) : [];
            setRoles(userRoles);
            // Marquer comme connecté
            localStorage.setItem('auth_status', 'logged_in');
            console.log('User logged in with roles:', userRoles);
        } catch (error) {
            // 401 is expected when user is not logged in, don't log it
            if (error.response?.status !== 401) {
                console.error('Error fetching user:', error);
            }
            setUser(null);
            setRoles([]);
            // En cas d'erreur (session expirée ou invalide), on nettoie le flag
            localStorage.removeItem('auth_status');
        } finally {
            setLoading(false);
        }
    };

    const initCsrf = async () => {
        try {
            // This GET request triggers Shield to set the XSRF-TOKEN cookie
            await axios.get('/csrf-token')
        } catch (e) {
            console.warn('CSRF init failed', e)
        }
    }

    useEffect(() => {
        // Initialise le cookie CSRF
        initCsrf().then(() => {
            // On ne tente de récupérer l'utilisateur que si on a le flag local
            // Cela évite l'erreur 401 pour les visiteurs publics
            const shouldFetch = localStorage.getItem('auth_status') === 'logged_in';

            if (shouldFetch) {
                fetchUser();
            } else {
                setLoading(false);
            }
        })
    }, []);


    const login = async (email, password) => {
        await axios.post('/auth/login', { email, password });
        localStorage.setItem('auth_status', 'logged_in');
        await fetchUser();
    };

    const register = async (fullName, email, password) => {
        await axios.post('/auth/register', { fullName, email, password });
        localStorage.setItem('auth_status', 'logged_in');
        await fetchUser();
    };

    const logout = async () => {
        try {
            await axios.post('/auth/logout');
        } finally {
            setUser(null);
            setRoles([]);
            localStorage.removeItem('auth_status');
        }
    };

    const hasRole = (roleName) => {
        return roles.includes(roleName);
    };

    const hasAnyRole = (roleNames) => {
        return roleNames.some(roleName => roles.includes(roleName));
    };

    const loginWithGoogle = () => {
        window.location.href = `${import.meta.env.VITE_API_URL || 'http://localhost:3333/api'}/auth/google/redirect`;
    };

    return (
        <AuthContext.Provider value={{ user, login, register, logout, loginWithGoogle, loading, roles, hasRole, hasAnyRole }}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
